import * as React from 'react';
import { Link, NavLink, useLocation } from "react-router-dom";

import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MenuIcon from '@mui/icons-material/Menu';
import { createTheme, ThemeProvider } from '@mui/material/styles';

import PersonIcon from '@mui/icons-material/Person';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import { darkAccentHover, darkAccent, lightText, mediumBlue, darkText } from '../theme'
import * as Color from '@mui/material/colors';
//import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

import { PageRoute } from "../AppRoutes";
import { ButtonLink } from './subs/ButtonLink';
import { UserContext, ThemeModes } from './UserProvider';
import { isAdminUser } from './types/User';
import { Divider } from '@mui/material';
//import { isAdminUser, isGlobalAdmin } from './types/User';

// global theming ruins the menu button, disable it here
const menuLightTheme = createTheme({
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    color: lightText,
                    textTransform: 'unset',
                    '&:hover': {
                        backgroundColor: darkAccentHover,
                    },
                    '&:link, &:visited, &:hover, &:active': {
                        color: `${lightText} !important`
                    },
                    '& a': {
                        color: `${lightText} !important`
                    },
                    '& .MuiSvgIcon-root': {
                        color: lightText
                    }
                },
            },
        },
        MuiDivider: {
            styleOverrides: {
                root: {
                    background: lightText,
                },
            },
        },
    }
});

const menuDarkTheme = createTheme({
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    color: lightText,
                    textTransform: 'unset',
                    '&:hover': {
                        backgroundColor: Color.grey[700],
                    },
                    '&:link, &:visited, &:hover, &:active': {
                        color: `${lightText} !important`
                    },
                    '& a': {
                        color: `${lightText} !important`
                    },
                    '& .MuiSvgIcon-root': {
                        color: lightText
                    }
                },
            },
        },
        MuiDivider: {
            styleOverrides: {
                root: {
                    background: lightText,
                },
            },
        },
    }
});

interface TopMenuProps {
   pages?: PageRoute[] | null
   title?: string | null
   subtitle?: string | null
}

export const TopMenu = ({ pages, title, subtitle }: TopMenuProps) => {
   const path = window.location.pathname;
   const userContext = React.useContext(UserContext);
   const [menuAnchor, setMenuAnchor] = React.useState<HTMLButtonElement | null>(null);
   const [isAdmin, setIsAdmin] = React.useState<boolean>();
   const [error, setError] = React.useState<string>();

   const location = useLocation();

   const onOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      setMenuAnchor(e.currentTarget);
   }

   const onClose = () => {
      setMenuAnchor(null);
   };

    React.useEffect(() => {
        if (!userContext?.user.id) {
            setIsAdmin(false);
            return;
        }
        isAdminUser(userContext.user.id, new AbortController()).then(isAdminResponse => setIsAdmin(isAdminResponse)).catch(errorResponse => setError(errorResponse));
   }, [userContext])

   return (
      <>
         {/*<Container sx={{ display: "flex", flexDirection: "row"}}>
            <Box ml={6} my={0} pt={2} sx={{ flexGrow: 0, display: { xs: 'none', md: 'flex' } }}>
               <img src='opclogo.png' alt='OPC Foundation' height={50} style={{
                  borderTop: '1px solid white',
                  borderLeft: '1px solid white',
                  borderBottom: '1px solid black',
                  borderRight: '1px solid black'
               }} />
            </Box>
            <Box ml={6} mr="auto" sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
               <Box ml={2}>
                  <Typography variant="subtitle1" component="div" noWrap>
                     {title}
                  </Typography>
                  <Typography variant="body1" component="div" sx={{ fontWeight: 'lighter', fontSize: 'smaller' }} noWrap>
                     {subtitle}
                  </Typography>
               </Box>
            </Box>
            </Container>*/}

         <Toolbar disableGutters sx={{ py: 0, width: "100%", maxWidth: 'none', mx: "auto", minHeight: '52px', display: "flex", justifyContent: "flex-end"}}>
            <Box ml={6} my={0} pt={2} sx={{ flexGrow: 0, display: { xs: 'none', md: 'flex' } }}>
               <img src='opclogo-white.png' alt='OPC Foundation' height={50} />
            </Box>
            <Box ml={6} mr="auto" sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
               <Box ml={2}>
                  <Typography variant="subtitle1" component="div" color='white' noWrap>
                     {title}
                  </Typography>
                  <Typography variant="body1" component="div" color='white' sx={{ fontWeight: 'lighter', fontSize: 'smaller'}} noWrap>
                     {subtitle}
                  </Typography>
               </Box>
            </Box>
            
            <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
               <IconButton
                  size="large"
                  aria-haspopup="true"
                  onClick={onOpen}
                  color="inherit"
                  sx={{ color: 'white' }}
               >
                  <MenuIcon />
               </IconButton>
               <Menu
                  id="menu-appbar"
                  anchorEl={menuAnchor}
                  anchorOrigin={{
                     vertical: 'bottom',
                     horizontal: 'left',
                  }}
                  keepMounted
                  transformOrigin={{
                     vertical: 'top',
                     horizontal: 'left',
                  }}
                  open={Boolean(menuAnchor)}
                  onClose={onClose}
                  sx={{
                     display: { xs: 'block', md: 'none' },
                  }}
               >
                  {pages?.map((page) => {
                     if (page.hidden) {
                        return null;
                     }
                     return (
                        <MenuItem key={page.name}>
                        <Link to={page.path}>{page.name}</Link>
                        </MenuItem>
                     );
                  })}
               </Menu>
            </Box>
            <Typography
               variant="h6"
               noWrap
               component="p"
               sx={{
                  mr: 2,
                  color: 'white', 
                  display: { xs: 'flex', md: 'none' },
                  flexGrow: 1
               }}
            >
               {title}
            </Typography>

            
            <Box ml={6} sx={{ flexGrow: 0, display: { xs: 'none', md: 'flex' } }}>
               {pages?.map((page) => {
                    if (page.hidden) {
                        return null;
                    }
                    if (page.administrator && !isAdmin) {
                        return null;
                    }
                    return (
                        <ThemeProvider theme={userContext.themeMode === ThemeModes.Dark ? menuDarkTheme : menuLightTheme}>
                        <Button
                            className={(page.path === path) ? "selected" : undefined}
                            key={page.name}
                            disableRipple={false}
                        >
                            <NavLink to={page.path} state={(page.modal?{background: location}:undefined)}>{page.name}</NavLink>
                        </Button>
                        <Divider orientation="vertical" variant="middle" flexItem />
                    </ThemeProvider>
                    );
               })}
            </Box>
            <Box sx={{ flexGrow: 0, pr: 4, display: 'flex', alignItems: 'center' }}>
                   {/*  <Button startIcon={<PictureAsPdfIcon/>}>Documentation</Button> */}
                <IconButton
                  aria-label="toggle dark mode"
                  onClick={() => userContext.setThemeMode(userContext.themeMode === ThemeModes.Dark ? ThemeModes.Light : ThemeModes.Dark)}
                  sx={{ color: 'white' }}
                >
                    {userContext.themeMode === ThemeModes.Dark ? <LightModeIcon /> : <DarkModeIcon />}
                </IconButton>
                <ThemeProvider theme={userContext.themeMode === ThemeModes.Dark ? menuDarkTheme : menuLightTheme}>
                    <Divider orientation="vertical" variant="middle" flexItem />
                    <ButtonLink startIcon={<PersonIcon />} to="/ProfileModal" state={{ background: location }} variant='text'>Profile</ButtonLink>
                </ThemeProvider>
               {/*<SettingsMenu />*/}
            </Box>
         </Toolbar>
      </>
   );
}
